/**
 * ReCharge Widget Customizer
 * 
 * This script customizes the ReCharge subscription widget by replacing the dropdown
 * with custom buttons for better user experience.
 * 
 * FIXES:
 * - Preserves the original price display in the subscription option when custom buttons are clicked
 * - Adds event listeners to ensure price elements are properly maintained during widget updates
 * - Prevents duplicate original price elements from being created
 */

class ReChargeWidgetCustomizer {
  constructor() {
    // Configuration
    this.containerSelector = '.rc-container-wrapper.rc_container_wrapper';
    this.selectSelector = '.rc-selling-plans__dropdown';
    this.buttonsContainerClass = 'recharge-custom-buttons';
    this.activeButtonClass = 'recharge-button-active';
    
    // Initialize the observer
    this.initObserver();

    // Observe rc_popup insertions/rerenders to ensure placement and icon replacement
    this.initRcPopupObserver();
    
    // Add event listener for ReCharge widget updates
    this.initWidgetUpdateListener();
  }

  /**
   * Should we move rc_popup based on viewport width?
   * On widths <= 749px we do not move rc_popup.
   * Aligns with theme CSS breakpoint (min-width: 750px).
   */
  shouldMoveRcPopup() {
    try {
      return window.matchMedia('(min-width: 750px)').matches;
    } catch (e) {
      return (window.innerWidth || 0) >= 750;
    }
  }

  /**
   * Find the product form element to anchor rc_popup on mobile (≤ 749px)
   */
  getMobileProductFormAnchor() {
    return (
      document.querySelector('product-form.product-form') ||
      document.querySelector('div.product-form') ||
      null
    );
  }

  /**
   * Place rc_popup according to viewport:
   * - ≥ 750px: after .subscription__advantages
   * - ≤ 749px: after product form anchor
   */
  placeRcPopupByViewport() {
    const rcPopup = document.querySelector('div.rc_popup');
    if (!rcPopup) return false;

    const isDesktop = this.shouldMoveRcPopup();
    if (isDesktop) {
      const advantagesEl = document.querySelector('.subscription__advantages');
      if (advantagesEl && advantagesEl.parentNode) {
        if (advantagesEl.nextElementSibling !== rcPopup) {
          advantagesEl.parentNode.insertBefore(rcPopup, advantagesEl.nextSibling);
        }
        this.replaceRcPopupIcon(rcPopup);
        setTimeout(() => this.replaceRcPopupIcon(rcPopup), 200);
        setTimeout(() => this.replaceRcPopupIcon(rcPopup), 600);
        return true;
      }
      return false;
    } else {
      const anchor = this.getMobileProductFormAnchor();
      if (anchor && anchor.parentNode) {
        if (anchor.nextElementSibling !== rcPopup) {
          anchor.parentNode.insertBefore(rcPopup, anchor.nextSibling);
        }
        this.replaceRcPopupIcon(rcPopup);
        setTimeout(() => this.replaceRcPopupIcon(rcPopup), 200);
        setTimeout(() => this.replaceRcPopupIcon(rcPopup), 600);
        return true;
      }
      return false;
    }
  }

  /**
   * Replace rc_popup reload icon SVG with custom SVG (idempotent)
   * @param {HTMLElement} rcPopup
   */
  replaceRcPopupIcon(rcPopup) {
    if (!rcPopup) return false;

    // If our custom icon is already present, do nothing
    const alreadyCustom = rcPopup.querySelector('svg.reload-icon[data-custom-icon="true"]');
    if (alreadyCustom) return true;

    const tryReplace = () => {
      const oldIcon = rcPopup.querySelector('svg.reload-icon');
      if (!oldIcon) return false;

      // Build new SVG, preserving the reload-icon class and data-test-popup-icon attribute
      const wrapper = document.createElement('div');
      wrapper.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="20" viewBox="0 0 17 20" fill="none" class="reload-icon" data-test-popup-icon="" data-custom-icon="true">\n  <path d="M12.666 1.66675L15.9993 5.00008L12.666 8.33341" stroke="#434444" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>\n  <path d="M1 9.16667V8.33333C1 7.44928 1.35119 6.60143 1.97631 5.97631C2.60143 5.35119 3.44928 5 4.33333 5H16M4.33333 18.3333L1 15L4.33333 11.6667" stroke="#434444" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>\n  <path d="M16 10.8335V11.6668C16 12.5509 15.6488 13.3987 15.0237 14.0239C14.3986 14.649 13.5507 15.0002 12.6667 15.0002H1" stroke="#434444" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>\n</svg>';
      const newIcon = wrapper.firstElementChild;
      if (!newIcon) return false;

      oldIcon.replaceWith(newIcon);
      return true;
    };

    // Try immediate and then with small delays if inner content renders later
    if (tryReplace()) return true;
    setTimeout(() => tryReplace(), 200);
    setTimeout(() => tryReplace(), 600);
    return false;
  }

  /**
   * Initialize MutationObserver to watch for the ReCharge widget
   */
  initObserver() {
    // Create a new MutationObserver
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Check if the ReCharge widget has been added to the page
          const rechargeWidget = document.querySelector(this.containerSelector);
          if (rechargeWidget) {
            // Check if the select dropdown exists
            const select = rechargeWidget.querySelector(this.selectSelector);
            if (select) {
              // Both widget and select found, process it
              this.processWidget(rechargeWidget, select);
              // Disconnect the observer once we've found both elements
              observer.disconnect();
              break;
            }
            // If select not found, continue observing
          }
        }
      }
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Process the ReCharge widget once it's found
   * @param {HTMLElement} widget - The ReCharge widget element
   * @param {HTMLElement} select - The select dropdown element
   */
  processWidget(widget, select) {
    // Select is already found and passed as parameter
    console.log('ReCharge widget and select dropdown found and being processed');

    // Add original price to subscription price
    this.addOriginalPriceToSubscription(widget);

    // Create container for custom buttons
    const buttonsContainer = document.createElement('div');
    buttonsContainer.className = this.buttonsContainerClass;
    
    // Get all options from the select
    const options = Array.from(select.options);
    
    // Create buttons for each option
    options.forEach((option, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'recharge-custom-button';
      button.textContent = option.textContent.trim();
      button.dataset.value = option.value;
      
      // Make the first button active by default
      if (index === 0) {
        button.classList.add(this.activeButtonClass);
      }
      
      // Add click event listener
      button.addEventListener('click', () => {
        this.handleButtonClick(button, select);
      });
      
      // Add button to container
      buttonsContainer.appendChild(button);
    });
    
    // Insert buttons after the select
    select.parentNode.insertBefore(buttonsContainer, select.nextSibling);

    // Inject subscription advantages markup right after the custom buttons (once)
    // Avoid duplicate insertion if already present
    const existingAdvantages = widget.querySelector('.subscription__advantages');
    if (!existingAdvantages) {
      const advantages = document.createElement('div');
      advantages.className = 'subscription__advantages';
      advantages.innerHTML = `
    <div class="subscription__advantage">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M7.5 15.5C8.48491 15.5 9.46018 15.306 10.3701 14.9291C11.2801 14.5522 12.1069 13.9997 12.8033 13.3033C13.4997 12.6069 14.0522 11.7801 14.4291 10.8701C14.806 9.96018 15 8.98491 15 8C15 7.01509 14.806 6.03982 14.4291 5.12987C14.0522 4.21993 13.4997 3.39314 12.8033 2.6967C12.1069 2.00026 11.2801 1.44781 10.3701 1.0709C9.46018 0.693993 8.48491 0.5 7.5 0.5C5.51088 0.5 3.60322 1.29018 2.1967 2.6967C0.790176 4.10322 0 6.01088 0 8C0 9.98912 0.790176 11.8968 2.1967 13.3033C3.60322 14.7098 5.51088 15.5 7.5 15.5ZM7.30667 11.0333L11.4733 6.03333L10.1933 4.96667L6.61 9.26583L4.75583 7.41083L3.5775 8.58917L6.0775 11.0892L6.7225 11.7342L7.30667 11.0333Z"
                  fill="#71BF44"/>
        </svg>
        <span>Pause or cancel anytime</span>
    </div>
    <div class="subscription__advantage">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M7.5 15.5C8.48491 15.5 9.46018 15.306 10.3701 14.9291C11.2801 14.5522 12.1069 13.9997 12.8033 13.3033C13.4997 12.6069 14.0522 11.7801 14.4291 10.8701C14.806 9.96018 15 8.98491 15 8C15 7.01509 14.806 6.03982 14.4291 5.12987C14.0522 4.21993 13.4997 3.39314 12.8033 2.6967C12.1069 2.00026 11.2801 1.44781 10.3701 1.0709C9.46018 0.693993 8.48491 0.5 7.5 0.5C5.51088 0.5 3.60322 1.29018 2.1967 2.6967C0.790176 4.10322 0 6.01088 0 8C0 9.98912 0.790176 11.8968 2.1967 13.3033C3.60322 14.7098 5.51088 15.5 7.5 15.5ZM7.30667 11.0333L11.4733 6.03333L10.1933 4.96667L6.61 9.26583L4.75583 7.41083L3.5775 8.58917L6.0775 11.0892L6.7225 11.7342L7.30667 11.0333Z"
                  fill="#71BF44"/>
        </svg>
        <span>Gifts & discounts on repeat orders</span>
    </div>`;

      // Place it immediately after the buttons container
      buttonsContainer.parentNode.insertBefore(advantages, buttonsContainer.nextSibling);

      // Move rc_popup element (if exists on the page) right after the advantages block
      const advantagesEl = advantages;
      const moveRcPopup = () => {
        return this.placeRcPopupByViewport();
      };

      // Try immediately and with small delays in case rc_popup is injected later
      if (!moveRcPopup()) {
        setTimeout(moveRcPopup, 300);
        setTimeout(moveRcPopup, 1000);
      }
    } else {
      // Advantages already exist; still ensure rc_popup is placed appropriately per viewport
      const advantagesEl = widget.querySelector('.subscription__advantages');
      if (advantagesEl) {
        this.placeRcPopupByViewport();
      }
    }
  }

  /**
   * Handle button click
   * @param {HTMLElement} clickedButton - The button that was clicked
   * @param {HTMLElement} select - The select element to update
   */
  handleButtonClick(clickedButton, select) {
    // Remove active class from all buttons
    const buttons = document.querySelectorAll(`.${this.buttonsContainerClass} .recharge-custom-button`);
    buttons.forEach(button => {
      button.classList.remove(this.activeButtonClass);
    });
    
    // Add active class to clicked button
    clickedButton.classList.add(this.activeButtonClass);
    
    // Update the select value
    select.value = clickedButton.dataset.value;
    
    // Trigger change event on select to ensure ReCharge widget updates
    const event = new Event('change', { bubbles: true });
    select.dispatchEvent(event);
    
    // FIX: The ReCharge widget resets the price elements when the select value changes.
    // We need to wait for the widget to update, then reapply the original price to the
    // subscription price element to preserve the functionality.
    setTimeout(() => {
      // Find the widget container again (it might have been updated)
      const widget = document.querySelector(this.containerSelector);
      if (widget) {
        this.addOriginalPriceToSubscription(widget);
      }
    }, 100); // Small delay to ensure the widget has updated
  }

  /**
   * Initialize event listener for ReCharge widget updates
   * 
   * FIX: This method adds global event listeners to ensure the original price
   * is preserved in the subscription option regardless of how the widget is updated.
   * This provides a more robust solution beyond just handling custom button clicks.
   */
  initRcPopupObserver() {
    const ensurePlacementAndIcon = () => {
      return this.placeRcPopupByViewport();
    };

    // Initial attempt
    setTimeout(ensurePlacementAndIcon, 0);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList' && (m.addedNodes?.length || m.removedNodes?.length)) {
          // If a rc_popup was added or re-rendered anywhere in the DOM
          const addedRcPopup = Array.from(m.addedNodes || []).some(n => n.nodeType === 1 && (n.matches?.('div.rc_popup') || n.querySelector?.('div.rc_popup')));
          const removedOrChanged = Array.from(m.removedNodes || []).some(n => n.nodeType === 1 && (n.matches?.('div.rc_popup') || n.querySelector?.('div.rc_popup')));
          if (addedRcPopup || removedOrChanged) {
            // Run in short delays to catch async inner HTML
            setTimeout(ensurePlacementAndIcon, 0);
            setTimeout(ensurePlacementAndIcon, 200);
            setTimeout(ensurePlacementAndIcon, 600);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Re-evaluate placement on viewport resize (debounced)
    let __rcResizeT;
    window.addEventListener('resize', () => {
      clearTimeout(__rcResizeT);
      __rcResizeT = setTimeout(() => {
        ensurePlacementAndIcon();
      }, 150);
    });
  }

  initWidgetUpdateListener() {
    // Listen for changes to the ReCharge widget through click events
    // This catches interactions like radio button selections and other UI elements
    document.addEventListener('click', (event) => {
      // Check if the click was on or inside the ReCharge widget
      if (event.target.closest(this.containerSelector)) {
        // Wait a short time for any updates to complete
        setTimeout(() => {
          const widget = document.querySelector(this.containerSelector);
          if (widget) {
            this.addOriginalPriceToSubscription(widget);
          }
          const rcPopup = document.querySelector('div.rc_popup');
          if (rcPopup) {
            this.replaceRcPopupIcon(rcPopup);
            setTimeout(() => this.replaceRcPopupIcon(rcPopup), 200);
            setTimeout(() => this.replaceRcPopupIcon(rcPopup), 600);
          }
        }, 100);
      }
    });
    
    // Also listen for any changes to the select element directly
    // This catches programmatic changes and native select interactions
    document.addEventListener('change', (event) => {
      if (event.target.matches(this.selectSelector)) {
        // Wait a short time for any updates to complete
        setTimeout(() => {
          const widget = document.querySelector(this.containerSelector);
          if (widget) {
            this.addOriginalPriceToSubscription(widget);
          }
          const rcPopup = document.querySelector('div.rc_popup');
          if (rcPopup) {
            this.replaceRcPopupIcon(rcPopup);
            setTimeout(() => this.replaceRcPopupIcon(rcPopup), 200);
            setTimeout(() => this.replaceRcPopupIcon(rcPopup), 600);
          }
        }, 100);
      }
    });
  }

  /**
   * Add original price to subscription price element
   * @param {HTMLElement} widget - The ReCharge widget element
   */
  addOriginalPriceToSubscription(widget) {
    // Find the subscription price element
    const subscriptionPriceElement = widget.querySelector('.rc-option__price.rc_widget__price.rc_widget__price--subsave');
    
    // Find the one-time purchase price element
    const onetimePriceElement = widget.querySelector('.rc-option__price.rc_widget__price.rc_widget__price--onetime');
    
    // If both elements exist
    if (subscriptionPriceElement && onetimePriceElement) {
      // Check if the original price span already exists
      const existingOriginalPrice = subscriptionPriceElement.querySelector('.rc-option__original-price');

      // Helper to normalize price text to a comparable numeric value
      const normalizePrice = (text) => {
        if (!text) return null;
        const cleaned = text
          .replace(/[^0-9.,]/g, '') // keep only digits, dots, commas
          .replace(/\s/g, '');
        // If there is both '.' and ',', assume ',' is thousands separator and remove commas
        if (cleaned.includes('.') && cleaned.includes(',')) {
          return parseFloat(cleaned.replace(/,/g, ''));
        }
        // If there's only a comma, treat it as decimal separator
        if (!cleaned.includes('.') && cleaned.includes(',')) {
          return parseFloat(cleaned.replace(',', '.'));
        }
        // Default: parse as standard decimal with optional thousands removed (none expected)
        return parseFloat(cleaned);
      };

      // Only add the original price if it doesn't already exist
      if (!existingOriginalPrice) {
        const originalPriceText = onetimePriceElement.textContent.trim();
        const subscriptionPriceText = subscriptionPriceElement.textContent.trim();

        const originalPriceValue = normalizePrice(originalPriceText);
        const subscriptionPriceValue = normalizePrice(subscriptionPriceText);

        // If prices are equal (or cannot be parsed distinctly), do not add the original price span
        if (
          originalPriceValue != null &&
          subscriptionPriceValue != null &&
          Math.abs(originalPriceValue - subscriptionPriceValue) < 0.0001
        ) {
          return; // Prices same, skip adding original price span
        }

        // Create a span for the original price
        const originalPriceSpan = document.createElement('span');
        originalPriceSpan.className = 'rc-option__original-price';
        originalPriceSpan.textContent = originalPriceText;

        subscriptionPriceElement.insertBefore(originalPriceSpan, subscriptionPriceElement.firstChild);
      }
    }
  }

}

document.addEventListener('DOMContentLoaded', () => {
  new ReChargeWidgetCustomizer();
});